import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Effect } from 'effect';
import { getClerkTokenEffect, restoreClerkTokenBridgeError } from '@/api/clerkTokenBridge';
import { withLoginRequiredPrompt } from '@/api/loginRequired';
import { API_ORIGIN } from '@/lib/host';
import { solveTurnstileEffect } from '@/lib/turnstile';
import { attemptApi, runApiSync } from './effect-runtime';
import {
  liveHtmlSessionResourceSchema,
  type LiveHtmlSessionResource,
} from '@okrapdf/schemas/live-html';

/**
 * Declarative data layer for the cloud-backed `/tools` runners (W.4.39) —
 * veraPDF Checker, PDF to JSON, PDF to Markdown, PDF to HTML, PDF to Link. Replaces the
 * runners' hand-rolled fetch + setInterval polling with RTK Query mutations
 * and pollable event queries, per the repo data-fetching rule (RTK Query for
 * server state).
 *
 * Endpoints are same-origin paths on the web Worker, which proxies
 * `/api/tools/*` to the playground runtime (guest-cookie sessions) and `/s`
 * to the live-doc service — so `credentials: 'include'` carries the guest
 * cookie, and a Clerk token rides along when the visitor is signed in.
 *
 * A.6.6c: paid runs require an account. Runners gate signed-out visitors
 * before calling these endpoints (`useLoginGate`); if the server still answers
 * `401 login_required`, `withLoginRequiredPrompt` opens sign-up and rewrites
 * the error to the server's human message.
 */

export type ToolsSessionResponse = {
  sessionId?: string;
  ok?: boolean;
  error?: string;
};

export const parseLiveHtmlSessionEffect = Effect.fn('web.toolsApi.parseLiveHtmlSession')(function* (
  value: unknown,
) {
  return yield* attemptApi('parse', 'toolsApi.liveHtmlSession', () =>
    liveHtmlSessionResourceSchema.parse(value),
  );
});

export type ToolRunEvent = {
  type: string;
  payload?: Record<string, unknown>;
  created_at?: string;
};

export type ToolRunEventsResponse = {
  runId: string;
  status?: string;
  events: ToolRunEvent[];
  error?: string;
};

export type VeraPdfRunStartResponse = {
  runId: string;
  workflowId?: string;
  docId?: string;
  fileName?: string;
  error?: string;
};

export type PdfToJsonRunStartResponse = {
  runId: string;
  sessionAgentId?: string;
  docId?: string;
  fileName?: string;
  document?: unknown;
  error?: string;
};

export type PdfToJsonUsage = {
  inputTokens?: number;
  outputTokens?: number;
  costUsd?: number;
  model?: string;
  durationMs?: number;
};

export type PdfToJsonRemaining = {
  extractRuns?: number;
  costUsd?: number;
  resetsAt?: string;
  gatedBy?: string;
};

export type PdfToJsonConfirmResponse = {
  json?: unknown;
  usage?: PdfToJsonUsage;
  remaining?: PdfToJsonRemaining;
  error?: string;
};

export type PdfToMarkdownUploadResponse = {
  docId?: string;
  fileName?: string;
  error?: string;
};

export type PdfToMarkdownRunStartResponse = {
  runId?: string;
  documentId?: string;
  variants?: string[];
  error?: string;
};

export type PdfToMarkdownEventsResponse = {
  data: ToolRunEvent[];
  error?: string;
};

export type PdfToMarkdownResultResponse = {
  documentId?: string;
  fileName?: string;
  variant?: string;
  markdown?: string;
  cached?: boolean;
  metrics?: {
    label?: string;
    costUsd?: number;
    pages?: number;
    elapsedMs?: number;
  };
  error?: string;
};

export type PdfToExcelRunStartResponse = {
  runId?: string;
  documentId?: string;
  variants?: string[];
  error?: string;
};

export type PdfToExcelResultResponse = {
  documentId?: string;
  fileName?: string;
  variant?: string;
  /** Table blocks as the parser stored them; the runner turns them into sheets. */
  tables?: unknown[];
  cached?: boolean;
  metrics?: {
    costUsd?: number;
    pages?: number;
    elapsedMs?: number;
  };
  error?: string;
};

export type PdfToHtmlPublishResponse = LiveHtmlSessionResource;

export type HostedPdfResource = {
  namespace?: string;
  /** `embed` is the link's `/embed` page (W.4.124 R2); null or absent hides the embed snippet. */
  urls?: { view?: string; pdf?: string; embed?: string | null };
  error?: string;
};

export type StudioRouteRequest = {
  prompt: string;
  tools: Array<{ id: string; title: string; description: string }>;
  document: { name: string; pageCount: number } | null;
  activeToolId: string | null;
};

export type StudioRouteResponse = {
  kind: 'route';
  toolId: string | null;
  reply: string;
  alternatives: string[];
};

export type StudioRewriteRequest = {
  text: string;
  instruction: 'fix' | 'shorten' | 'clarify';
  maxChars: number;
};

export type StudioRewriteResponse = {
  kind: 'rewrite';
  text: string;
};

export const toolsApi = createApi({
  reducerPath: 'toolsApi',
  baseQuery: withLoginRequiredPrompt(fetchBaseQuery({
    baseUrl: '/',
    credentials: 'include',
    prepareHeaders: async (headers, api) => {
      const token = await Effect.runPromise(restoreClerkTokenBridgeError(getClerkTokenEffect()));
      // An endpoint that names its own bearer (publishHostedPdf after a token
      // refresh) keeps it; the cached session token only fills a missing header.
      if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
      headers.set('Accept', 'application/json');
      // W.4.48 — solve one invisible Turnstile challenge when the guest
      // session is established, so the paid tool endpoints can tell a browser
      // from a script. Session bootstrap is the only place this runs: the
      // server marks the session once and reads the flag thereafter, so the
      // per-run path stays free of extra latency. Signed-in callers are
      // exempt (they bill against a real balance), and an unconfigured or
      // failed challenge yields no header at all.
      if (api.endpoint === 'createToolsSession' && !token) {
        const turnstileToken = await Effect.runPromise(solveTurnstileEffect());
        if (turnstileToken) headers.set('CF-Turnstile-Response', turnstileToken);
      }
      return headers;
    },
  }), 'tools'),
  endpoints: (build) => ({
    /** Establish (or rotate) the anonymous guest session the tool runs bill against. */
    createToolsSession: build.mutation<ToolsSessionResponse, { sessionId?: string } | void>({
      query: (args) => ({
        url: '/api/tools/session',
        method: 'POST',
        body: args?.sessionId ? { sessionId: args.sessionId } : undefined,
      }),
    }),

    startVeraPdfRun: build.mutation<VeraPdfRunStartResponse, { file: File }>({
      query: ({ file }) => {
        const body = new FormData();
        body.append('file', file);
        return { url: '/api/tools/verapdf-checker/runs', method: 'POST', body };
      },
    }),

    /** Pollable progress/event log for a veraPDF run — drive with `pollingInterval`. */
    getVeraPdfRunEvents: build.query<ToolRunEventsResponse, string>({
      query: (runId) => `/api/tools/verapdf-checker/runs/${encodeURIComponent(runId)}/events`,
    }),

    startPdfToJsonRun: build.mutation<PdfToJsonRunStartResponse, { file: File; prompt?: string }>({
      query: ({ file, prompt }) => {
        const body = new FormData();
        body.append('file', file);
        if (prompt) body.append('prompt', prompt);
        return { url: '/api/tools/pdf-to-json/runs', method: 'POST', body };
      },
    }),

    /** Pollable progress/event log for a PDF→JSON run — drive with `pollingInterval`. */
    getPdfToJsonRunEvents: build.query<ToolRunEventsResponse, string>({
      query: (runId) => `/api/tools/pdf-to-json/runs/${encodeURIComponent(runId)}/events`,
    }),

    confirmPdfToJsonSchema: build.mutation<
      PdfToJsonConfirmResponse,
      { runId: string; schema: unknown }
    >({
      query: ({ runId, schema }) => ({
        url: `/api/tools/pdf-to-json/runs/${encodeURIComponent(runId)}/confirm`,
        method: 'POST',
        body: { schema },
      }),
    }),

    uploadPdfForMarkdown: build.mutation<PdfToMarkdownUploadResponse, { file: File }>({
      query: ({ file }) => {
        const body = new FormData();
        body.append('file', file);
        return { url: '/api/tools/upload', method: 'POST', body };
      },
    }),

    startPdfToMarkdownRun: build.mutation<PdfToMarkdownRunStartResponse, { docId: string }>({
      query: ({ docId }) => ({
        url: '/api/tools/pdf-to-markdown/runs',
        method: 'POST',
        body: { docId, variants: ['vlm-parsebench'] },
      }),
    }),

    getPdfToMarkdownEvents: build.query<PdfToMarkdownEventsResponse, string>({
      query: () => '/api/tools/pdf-to-markdown/events',
    }),

    getPdfToMarkdownResult: build.query<PdfToMarkdownResultResponse, string>({
      query: (docId) => ({
        url: '/api/tools/pdf-to-markdown/result',
        params: { docId, variant: 'vlm-parsebench' },
      }),
    }),

    /** The server pins the LlamaParse mode; the browser never names a parser. */
    startPdfToExcelRun: build.mutation<PdfToExcelRunStartResponse, { docId: string }>({
      query: ({ docId }) => ({
        url: '/api/tools/pdf-to-excel/runs',
        method: 'POST',
        body: { docId },
      }),
    }),

    getPdfToExcelEvents: build.query<PdfToMarkdownEventsResponse, string>({
      query: () => '/api/tools/pdf-to-excel/events',
    }),

    getPdfToExcelResult: build.query<PdfToExcelResultResponse, string>({
      query: (docId) => ({
        url: '/api/tools/pdf-to-excel/result',
        params: { docId },
      }),
    }),

    /** The server pins the LlamaParse mode here too. */
    startHandwritingOcrRun: build.mutation<PdfToMarkdownRunStartResponse, { docId: string }>({
      query: ({ docId }) => ({
        url: '/api/tools/handwriting-ocr/runs',
        method: 'POST',
        body: { docId },
      }),
    }),

    getHandwritingOcrEvents: build.query<PdfToMarkdownEventsResponse, string>({
      query: () => '/api/tools/handwriting-ocr/events',
    }),

    getHandwritingOcrResult: build.query<PdfToMarkdownResultResponse, string>({
      query: (docId) => ({
        url: '/api/tools/handwriting-ocr/result',
        params: { docId },
      }),
    }),

    /**
     * Create a private live HTML page. API callers negotiate a typed JSON
     * receipt; native HTML forms keep the no-JS 303 redirect behavior.
     * `POST /s` requires `Authorization: Bearer <Clerk session token>`
     * (A.6.6c); `prepareHeaders` attaches it, and the web Worker's `/s`
     * pass-through forwards request headers untouched.
     */
    publishPdfToHtml: build.mutation<PdfToHtmlPublishResponse, { file?: File; url?: string }>({
      query: ({ file, url }) => {
        const body = new FormData();
        if (file) body.append('file', file);
        if (url) body.append('url', url);
        return { url: '/s', method: 'POST', body };
      },
      transformResponse: (data: unknown) => runApiSync(parseLiveHtmlSessionEffect(data)),
    }),

    /**
     * Hosted "PDF to Link" publish (signed-in users). The runner passes the
     * Clerk token it read, so a retry after `401 invalid_token` sends the fresh
     * token instead of the cached one prepareHeaders would attach.
     */
    publishHostedPdf: build.mutation<
      HostedPdfResource,
      { file: File; payload: unknown; token?: string }
    >({
      query: ({ file, payload, token }) => {
        const body = new FormData();
        body.append('file', file);
        body.append('payload', JSON.stringify(payload));
        return {
          url: `${API_ORIGIN}/v1/host/playground`,
          method: 'POST',
          body,
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          credentials: 'omit',
          cache: 'no-store',
        };
      },
    }),

    /**
     * Okra Agent (W.4.105): pick the Studio tool for a sentence. The body
     * carries the prompt, the tool catalogue, and the open file's name and
     * page count — never the file. `POST /api/ai/studio` requires
     * `Authorization: Bearer <Clerk session token>`; `prepareHeaders` attaches
     * it. The Studio sits behind the app sign-in gate, so a `401
     * login_required` here is an expired session: the base query opens
     * sign-up once and callers must not prompt again.
     */
    studioRoute: build.mutation<StudioRouteResponse, StudioRouteRequest>({
      query: (body) => ({
        url: '/api/ai/studio',
        method: 'POST',
        body: { kind: 'route', ...body },
      }),
    }),

    /** Okra Agent (W.4.105): rewrite one Edit text block. Same endpoint and auth as `studioRoute`. */
    studioRewrite: build.mutation<StudioRewriteResponse, StudioRewriteRequest>({
      query: (body) => ({
        url: '/api/ai/studio',
        method: 'POST',
        body: { kind: 'rewrite', ...body },
      }),
    }),
  }),
});

export const {
  useCreateToolsSessionMutation,
  useStartVeraPdfRunMutation,
  useGetVeraPdfRunEventsQuery,
  useLazyGetVeraPdfRunEventsQuery,
  useStartPdfToJsonRunMutation,
  useGetPdfToJsonRunEventsQuery,
  useLazyGetPdfToJsonRunEventsQuery,
  useConfirmPdfToJsonSchemaMutation,
  useUploadPdfForMarkdownMutation,
  useStartPdfToMarkdownRunMutation,
  useGetPdfToMarkdownEventsQuery,
  useLazyGetPdfToMarkdownResultQuery,
  useStartPdfToExcelRunMutation,
  useGetPdfToExcelEventsQuery,
  useLazyGetPdfToExcelResultQuery,
  useStartHandwritingOcrRunMutation,
  useGetHandwritingOcrEventsQuery,
  useLazyGetHandwritingOcrResultQuery,
  usePublishPdfToHtmlMutation,
  usePublishHostedPdfMutation,
  useStudioRouteMutation,
  useStudioRewriteMutation,
} = toolsApi;
